import type * as LabelPrimitive from "@radix-ui/react-label"
import { Slot } from "@radix-ui/react-slot"
import * as React from "react"
import type {
  ControllerProps,
  FieldPath,
  FieldValues,
  UseFormReturn,
} from "react-hook-form"
import { Controller, FormProvider, useFormContext } from "react-hook-form"

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { env } from "@/lib/env"
import { formLogger } from "@/lib/logger"
import { cn } from "@/lib/utils"
import { Label } from "./label"

type TFormElProps<TFieldValues extends FieldValues> = {
  children: React.ReactNode
  onSubmit: (data: TFieldValues) => Promise<void> | void
  className?: string
}

const FormEl = <TFieldValues extends FieldValues>({
  children,
  onSubmit,
  className,
}: TFormElProps<TFieldValues>) => {
  const { handleSubmit } = useFormContext<TFieldValues>()

  return (
    <form
      onSubmit={handleSubmit(
        (data) => {
          // Remove debugging in production - only log in development
          if (env.isDevelopment) {
            formLogger.debug("Form submission", data)
          }
          return onSubmit(data)
        },
        (error) => {
          // Secure error logging - don't expose sensitive data
          formLogger.formError("Form validation failed", error)
        },
      )}
      className={className}
    >
      {children}
    </form>
  )
}

type TFormProps<TFieldValues extends FieldValues> =
  UseFormReturn<TFieldValues> & {
    children: React.ReactNode
    onSubmit: (data: TFieldValues) => Promise<void> | void
    className?: string
  }
const Form = <TFieldValues extends FieldValues>({
  children,
  onSubmit,
  className,
  ...formMethods
}: TFormProps<TFieldValues>) => {
  return (
    <FormProvider {...formMethods}>
      <FormEl onSubmit={onSubmit} className={className}>
        {children}
      </FormEl>
    </FormProvider>
  )
}

type TFormFieldContextValue<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  name: TName
}

const FormFieldContext = React.createContext<TFormFieldContextValue>(
  {} as TFormFieldContextValue,
)

const FormField = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  ...props
}: ControllerProps<TFieldValues, TName>) => {
  const value = React.useMemo(() => ({ name: props.name }), [props.name])

  return (
    <FormFieldContext.Provider value={value}>
      <Controller {...props} />
    </FormFieldContext.Provider>
  )
}

const useFormField = () => {
  const fieldContext = React.useContext(FormFieldContext)
  const itemContext = React.useContext(FormItemContext)
  const { getFieldState, formState } = useFormContext()

  const fieldState = getFieldState(fieldContext.name, formState)

  if (!fieldContext) {
    throw new Error("useFormField should be used within <FormField>")
  }

  const { id } = itemContext

  return {
    id,
    name: fieldContext.name,
    formItemId: `${id}-form-item`,
    formDescriptionId: `${id}-form-item-description`,
    formMessageId: `${id}-form-item-message`,
    ...fieldState,
  }
}

type TFormItemContextValue = {
  id: string
}

const FormItemContext = React.createContext<TFormItemContextValue>(
  {} as TFormItemContextValue,
)

const FormItem = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => {
  const id = React.useId()
  const value = React.useMemo(() => ({ id }), [id])

  return (
    <FormItemContext.Provider value={value}>
      <div className={cn("space-y-2", className)} {...props} />
    </FormItemContext.Provider>
  )
}
FormItem.displayName = "FormItem"

const FormLabel = ({
  className,
  ...props
}: React.ComponentPropsWithoutRef<typeof LabelPrimitive.Root>) => {
  const { error, formItemId } = useFormField()

  return (
    <Label
      className={cn(error && "text-rose-500", className)}
      htmlFor={formItemId}
      {...props}
    />
  )
}
FormLabel.displayName = "FormLabel"

const FormControl = (props: React.ComponentPropsWithoutRef<typeof Slot>) => {
  const { error, formItemId, formDescriptionId, formMessageId } = useFormField()

  return (
    <Slot
      id={formItemId}
      aria-describedby={
        !error
          ? `${formDescriptionId}`
          : `${formDescriptionId} ${formMessageId}`
      }
      aria-invalid={!!error}
      {...props}
    />
  )
}
FormControl.displayName = "FormControl"

const FormDescription = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLParagraphElement>) => {
  const { formDescriptionId } = useFormField()

  return (
    <p
      id={formDescriptionId}
      className={cn("text-muted-foreground text-[0.8rem]", className)}
      {...props}
    />
  )
}
FormDescription.displayName = "FormDescription"

const FormMessage = ({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLParagraphElement>) => {
  const { error, formMessageId, name } = useFormField()
  let body = error ? String(error?.message) : children

  if (!body) {
    return null
  }

  // Define a type for nested field errors
  type NestedFieldError = {
    [key: string]: { message?: string }
  }

  if (error && !("message" in error) && name in error) {
    body = (error as NestedFieldError)[name].message
      ? String((error as NestedFieldError)[name].message)
      : children
  }

  return (
    <p
      id={formMessageId}
      className={cn("text-[0.8rem] font-medium text-rose-600", className)}
      {...props}
    >
      {body}
    </p>
  )
}
FormMessage.displayName = "FormMessage"

const FormInput = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  name,
  label,
  helper,
  children,
  required,
  readOnly,
  className,
  posLabel,
  disabled,
  ...props
}: Omit<ControllerProps<TFieldValues, TName>, "render"> & {
  label?: string
  className?: string
  required?: boolean
  readOnly?: boolean
  helper?: string | React.ReactNode
  children: React.ReactElement
  posLabel?: React.ReactNode
}) => {
  return (
    <FormField
      {...props}
      name={name}
      render={({ field }) => (
        <FormItem className={className}>
          <div className="flex items-center">
            {label && (
              <FormLabel>
                {label}
                {required && <span className="text-rose-600">*</span>}
              </FormLabel>
            )}
            {posLabel}
          </div>
          <FormControl>
            {React.cloneElement(children as any, {
              ...field,
              name,
              id: name,
              value: field.value ?? "",
              required,
              readOnly: readOnly || disabled,
              disabled,
            })}
          </FormControl>
          {helper && typeof helper === "string" && (
            <FormDescription>{helper}</FormDescription>
          )}
          {helper && typeof helper !== "string" && helper}
          <FormMessage />
        </FormItem>
      )}
    />
  )
}

const FormSelect = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  name,
  label,
  helper,
  required,
  className,
  posLabel,
  disabled,
  options,
  placeholder,
  ...props
}: Omit<ControllerProps<TFieldValues, TName>, "render"> & {
  label?: string
  className?: string
  required?: boolean
  helper?: string | React.ReactNode
  posLabel?: React.ReactNode
  disabled?: boolean
  placeholder?: string
  options: { value: string; label: string }[]
}) => {
  return (
    <FormField
      {...props}
      name={name}
      render={({ field }) => (
        <FormItem className={className}>
          <div className="flex items-center">
            {label && (
              <FormLabel>
                {label}
                {required && <span className="text-rose-600">*</span>}
              </FormLabel>
            )}
            {posLabel}
          </div>
          <FormControl>
            <Select
              {...field}
              onValueChange={field.onChange}
              disabled={disabled}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder={placeholder} />
              </SelectTrigger>
              <SelectContent>
                {options.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormControl>
          {helper && typeof helper === "string" && (
            <FormDescription>{helper}</FormDescription>
          )}
          {helper && typeof helper !== "string" && helper}
          <FormMessage />
        </FormItem>
      )}
    />
  )
}

const FormCheckbox = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  name,
  label,
  helper,
  children,
  className,
  disabled,
  ...props
}: Omit<ControllerProps<TFieldValues, TName>, "render"> & {
  label?: string | React.ReactNode
  className?: string
  helper?: string | React.ReactNode
  children: React.ReactElement
  disabled?: boolean
}) => {
  return (
    <FormField
      {...props}
      name={name}
      render={({ field }) => (
        <FormItem className={className}>
          <div className="flex items-center space-x-2">
            <FormControl>
              {React.cloneElement(children as any, {
                checked: field.value,
                onCheckedChange: field.onChange,
                disabled,
              })}
            </FormControl>
            {label && (
              <FormLabel className="mt-0! cursor-pointer">{label}</FormLabel>
            )}
          </div>
          {helper && typeof helper === "string" && (
            <FormDescription>{helper}</FormDescription>
          )}
          {helper && typeof helper !== "string" && helper}
          <FormMessage />
        </FormItem>
      )}
    />
  )
}

const FormTextarea = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  name,
  label,
  helper,
  children,
  required,
  readOnly,
  className,
  posLabel,
  disabled,
  ...props
}: Omit<ControllerProps<TFieldValues, TName>, "render"> & {
  label?: string
  className?: string
  required?: boolean
  readOnly?: boolean
  helper?: string | React.ReactNode
  children: React.ReactElement
  posLabel?: React.ReactNode
  disabled?: boolean
}) => {
  return (
    <FormField
      {...props}
      name={name}
      render={({ field }) => (
        <FormItem className={className}>
          <div className="flex items-center">
            {label && (
              <FormLabel>
                {label}
                {required && <span className="text-rose-600">*</span>}
              </FormLabel>
            )}
            {posLabel}
          </div>
          <FormControl>
            {React.cloneElement(children as any, {
              ...field,
              name,
              id: name,
              value: field.value ?? "",
              required,
              readOnly: readOnly || disabled,
              disabled,
            })}
          </FormControl>
          {helper && typeof helper === "string" && (
            <FormDescription>{helper}</FormDescription>
          )}
          {helper && typeof helper !== "string" && helper}
          <FormMessage />
        </FormItem>
      )}
    />
  )
}

export {
  useFormField,
  Form,
  FormItem,
  FormLabel,
  FormControl,
  FormDescription,
  FormMessage,
  FormField,
  FormInput,
  FormSelect,
  FormCheckbox,
  FormTextarea,
}
